import { useEffect, useState } from 'react';
import { axiosInstance } from 'utils/axios';

// material-ui
import {
    Box,
    Button,
    Grid,
    Typography,
    TextField,
    Select,
    MenuItem,
    Autocomplete,
    CircularProgress,
    Checkbox,
    FormControl
} from '@mui/material';

import { useTheme } from '@mui/material/styles';
import { deepPurple } from '@mui/material/colors';
import { gridSpacing } from 'app/constant';
import { useSelector } from 'react-redux';
import { selectCurrentToken } from 'features/auth/authSlice';
import StickyHeadTable from './components/StickyHeadTable';
import { errorAlert } from 'ui-component/Toasts';
import dayjs from 'dayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import useFetchData from 'hooks/useFetchData';
import CreateNotification from './components/CreateNotification';

const Notification = () => {
    const token = useSelector(selectCurrentToken);

    const [notifications, setNotifications] = useState([]);
    const [filterDateFrom, setFilterDateFrom] = useState(dayjs().subtract(5, 'days'));
    const [filterDateTo, setFilterDateTo] = useState(dayjs());
    const [filterCsUsername, setFilterCsUsername] = useState();
    const [filterCustomerLoginUsername, setFilterCustomerLoginUsername] = useState();

    const [isLoading, setLoading] = useState(false);

    const fetchNotifications = async () => {
        let notificationsURL = `/Notifications/All?`;

        if (filterDateFrom !== '') {
            notificationsURL += `dateFrom=${filterDateFrom.format('YYYY-MM-DD')}`;
        }
        if (filterDateTo !== '') {
            notificationsURL += `&dateTo=${filterDateTo.format('YYYY-MM-DD')}`;
        }
        if (filterCsUsername !== undefined) {
            notificationsURL += `&csUserName=${filterCsUsername}`;
        }
        if (filterCustomerLoginUsername !== undefined) {
            notificationsURL += `&customerLoginUserName=${filterCustomerLoginUsername}`;
        }

        setLoading(true);
        try {
            const res = await axiosInstance.get(notificationsURL, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (res.status === 200) {
                setNotifications(res.data);
            } else {
                errorAlert('Something went wrong.');
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const { data: notificationTypes } = useFetchData('/NotificationTypes/All');
    const { data: customerLoginUserNameOptions } = useFetchData('/Customer/LoginUserNames');
    const { data: userNameOptions } = useFetchData('/UserNames/Options');
    const { data: redirectTypes } = useFetchData('/Notifications/RedirectTypes/All');

    return (
        <Grid container item xs={12} padding="20px">
            <Grid item container xs={12} spacing={gridSpacing} sx={{ marginBottom: '10px', overflowY: 'auto' }}>
                <Grid item container xs={12} md={12} lg={12}>
                    <CreateNotification
                        notificationTypes={notificationTypes}
                        customerLoginUserNameOptions={customerLoginUserNameOptions}
                        onCreate={fetchNotifications}
                        userNameOptions={userNameOptions}
                        redirectTypes={redirectTypes}
                    />
                </Grid>

                <Grid item container xs={12} md={6} lg={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '16px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            Date From :
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={filterDateFrom}
                                slotProps={{
                                    textField: {
                                        error: false
                                    }
                                }}
                                onChange={(newValue) => setFilterDateFrom(newValue)}
                            />
                        </LocalizationProvider>
                    </Box>
                </Grid>

                <Grid item container xs={12} md={6} lg={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '16px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            Date To :
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={filterDateTo}
                                slotProps={{
                                    textField: {
                                        error: false
                                    }
                                }}
                                onChange={(newValue) => setFilterDateTo(newValue)}
                            />
                        </LocalizationProvider>
                    </Box>
                </Grid>

                <Grid item container xs={12} md={6} lg={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '16px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            CS UserName :
                        </Typography>
                        <Autocomplete
                            freeSolo
                            value={filterCsUsername}
                            onChange={(event, newValue) => {
                                setFilterCsUsername(newValue);
                            }}
                            inputValue={filterCsUsername}
                            onInputChange={(event, newInputValue) => {
                                setFilterCsUsername(newInputValue);
                            }}
                            options={userNameOptions}
                            sx={{ width: 170 }}
                            renderInput={(params) => <TextField {...params} />}
                            disableClearable
                        />
                    </Box>
                </Grid>

                <Grid item container xs={12} md={6} lg={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '16px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            Login UserName :
                        </Typography>
                        <Autocomplete
                            freeSolo
                            value={filterCustomerLoginUsername}
                            onChange={(event, newValue) => {
                                setFilterCustomerLoginUsername(newValue);
                            }}
                            inputValue={filterCustomerLoginUsername}
                            onInputChange={(event, newInputValue) => {
                                setFilterCustomerLoginUsername(newInputValue);
                            }}
                            options={customerLoginUserNameOptions}
                            sx={{ width: 170 }}
                            renderInput={(params) => <TextField {...params} />}
                            disableClearable
                        />
                    </Box>
                </Grid>

                <Grid item container xs={12} md={12} lg={12}>
                    <Button
                        variant="contained"
                        sx={{
                            float: 'right',
                            display: 'block',
                            mx: 'auto',
                            width: '150px',
                            backgroundColor: deepPurple[500],
                            marginTop: '20px',
                            marginBottom: '30px'
                        }}
                        onClick={() => fetchNotifications()}
                        disabled={isLoading}
                    >
                        Filter
                    </Button>
                </Grid>
                <Box sx={{ width: '100%', minHeight: '200px' }}>
                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <StickyHeadTable
                            notifications={notifications}
                            notificationTypes={notificationTypes}
                            customerLoginUserNameOptions={customerLoginUserNameOptions}
                            fetchNotifications={fetchNotifications}
                            userNameOptions={userNameOptions}
                            redirectTypes={redirectTypes}
                        />
                    )}
                </Box>
            </Grid>
        </Grid>
    );
};

export default Notification;
